import { Trans, useLingui } from '@lingui/react/macro'
import { Text } from 'designSystem/components/Text'
import type { LotoReportScope } from 'features/lotoReports/lotoReportSchemas'
import type { VisitLotoPage } from 'features/lotoReports/useLotoDocumentTab'
import {
  type LotoTargetDrawing,
  useLotoTargetDrawings,
} from 'features/lotoReports/useLotoTargetDrawings'
import { ArrowRight, MapPin } from 'lucide-react'
import type { ReactElement } from 'react'

import {
  LotoTargetDrawingButton,
  LotoTargetDrawingHere,
  LotoTargetDrawingList,
  LotoTargetDrawingText,
} from './lotoPagePanel.styles'

type LotoTargetDrawingsProps = {
  id: string
  name: string
  scope: LotoReportScope
  nodeId: string
  onVisitPage: VisitLotoPage
}

function DrawingRow({
  drawing,
  onVisitPage,
}: {
  drawing: LotoTargetDrawing
  onVisitPage: VisitLotoPage
}): ReactElement {
  const { t } = useLingui()
  const { key: drawingNumber, openId, pageNumber, label, isHere } = drawing
  const place =
    drawingNumber === openId
      ? t`Page ${pageNumber}`
      : t`${drawingNumber} · page ${pageNumber}`
  return (
    <li>
      <LotoTargetDrawingButton
        type='button'
        aria-current={isHere ? 'page' : undefined}
        disabled={isHere}
        onClick={() => onVisitPage({ documentId: openId, pageNumber })}>
        <LotoTargetDrawingText>
          <Text as='span' variant='caption' weight='medium' truncate>
            {label}
          </Text>
          <Text as='span' variant='caption' color='muted' truncate>
            {place}
          </Text>
        </LotoTargetDrawingText>
        {isHere ? (
          <LotoTargetDrawingHere>
            <MapPin />
            <Text as='span' variant='caption' weight='medium' wrap='nowrap'>
              <Trans>You are here</Trans>
            </Text>
          </LotoTargetDrawingHere>
        ) : (
          <ArrowRight aria-hidden />
        )}
      </LotoTargetDrawingButton>
    </li>
  )
}

export function LotoTargetDrawings({
  id,
  name,
  scope,
  nodeId,
  onVisitPage,
}: LotoTargetDrawingsProps): ReactElement {
  const { t } = useLingui()
  const { drawings, isLoading, error } = useLotoTargetDrawings(scope, nodeId)
  if (error)
    return (
      <Text id={id} variant='caption' color='destructive' role='alert'>
        <Trans>Could not load the drawings for this equipment.</Trans>
      </Text>
    )
  if (isLoading)
    return (
      <Text id={id} variant='caption' color='muted' role='status'>
        <Trans>Loading drawings…</Trans>
      </Text>
    )
  if (drawings.length === 0)
    return (
      <Text id={id} variant='caption' color='muted'>
        <Trans>No drawings show this equipment.</Trans>
      </Text>
    )
  return (
    <LotoTargetDrawingList id={id} aria-label={t`Drawings showing ${name}`}>
      {drawings.map((drawing) => (
        <DrawingRow
          key={drawing.openId}
          drawing={drawing}
          onVisitPage={onVisitPage}
        />
      ))}
    </LotoTargetDrawingList>
  )
}
